import {
  GRID_TILE_RATIO,
  getOutputPreset,
  OutputPresetId,
  type FixedOutputPresetId,
} from '@/content/instagram'
import {
  SplitKind,
  type CarouselTileCount,
  type GridRowCount,
  type OutputSettings,
  type SplitSettings,
} from '@/core/document/types'
import { assertPositiveSize, type AspectRatio, type Rect, type Size } from '@/core/geometry'

export const DEFAULT_OUTPUT_BACKGROUND = '#ffffff'
export const CAROUSEL_TILE_COUNTS = [2, 3, 4, 5, 6, 7, 8, 9, 10] as const satisfies readonly CarouselTileCount[]
export const GRID_ROW_COUNTS = [1, 2, 3] as const satisfies readonly GridRowCount[]
export const CAROUSEL_TILE_RATIOS = [
  { w: 4, h: 5 },
  { w: 1, h: 1 },
] as const satisfies readonly AspectRatio[]
export { GRID_TILE_RATIO } from '@/content/instagram'

export interface OutputContainLayout {
  size: Size
  rootRect: Rect
}

function ratioEquals(first: AspectRatio, second: AspectRatio): boolean {
  return first.w * second.h === second.w * first.h
}

function requireRatio(value: unknown): AspectRatio {
  if (typeof value !== 'object' || value === null) {
    throw new Error('Carousel split tile ratio must be an object')
  }
  const ratio = value as { w?: unknown; h?: unknown }
  if (typeof ratio.w !== 'number' || typeof ratio.h !== 'number'
    || !Number.isFinite(ratio.w) || !Number.isFinite(ratio.h)
    || ratio.w <= 0 || ratio.h <= 0) {
    throw new Error('Carousel split tile ratio must have finite positive sides')
  }
  return { w: ratio.w, h: ratio.h }
}

export function assertSplitSettings(value: unknown): asserts value is SplitSettings | null {
  if (value === null) return
  if (typeof value !== 'object') throw new Error('Output split must be null or an object')
  const split = value as { kind?: unknown; tiles?: unknown; tileRatio?: unknown; rows?: unknown }
  switch (split.kind) {
    case SplitKind.Carousel: {
      if (!CAROUSEL_TILE_COUNTS.includes(split.tiles as CarouselTileCount)) {
        throw new Error(`Carousel split tile count is invalid: "${String(split.tiles)}"`)
      }
      const ratio = requireRatio(split.tileRatio)
      if (!CAROUSEL_TILE_RATIOS.some((allowed) => ratioEquals(ratio, allowed))) {
        throw new Error(`Carousel split tile ratio ${ratio.w}:${ratio.h} is not supported`)
      }
      return
    }
    case SplitKind.Grid:
      if (!GRID_ROW_COUNTS.includes(split.rows as GridRowCount)) {
        throw new Error(`Grid split row count is invalid: "${String(split.rows)}"`)
      }
      return
    default:
      throw new Error(`Output split kind is unknown: "${String(split.kind)}"`)
  }
}

export function splitRatio(split: SplitSettings): AspectRatio {
  assertSplitSettings(split)
  switch (split.kind) {
    case SplitKind.Carousel:
      return { w: split.tiles * split.tileRatio.w, h: split.tileRatio.h }
    case SplitKind.Grid:
      return { w: 3 * GRID_TILE_RATIO.w, h: split.rows * GRID_TILE_RATIO.h }
  }
}

export function outputRatio(output: OutputSettings, originalRatio?: AspectRatio): AspectRatio {
  assertSplitSettings(output.split)
  if (output.split) return splitRatio(output.split)
  const preset = getOutputPreset(output.presetId)
  if (preset.ratio) return { ...preset.ratio }
  if (!originalRatio || !Number.isFinite(originalRatio.w) || !Number.isFinite(originalRatio.h)
    || originalRatio.w <= 0 || originalRatio.h <= 0) {
    throw new Error('Original output ratio requires a finite positive intrinsic ratio')
  }
  return { ...originalRatio }
}

export function outputRatioLabel(output: OutputSettings): string {
  if (!output.split) return getOutputPreset(output.presetId).ratioLabel
  const ratio = splitRatio(output.split)
  return `${ratio.w}:${ratio.h}`
}

export function outputContainLayout(rootSize: Size, ratio: AspectRatio): OutputContainLayout {
  assertPositiveSize(rootSize, 'Output root')
  if (!Number.isFinite(ratio.w) || !Number.isFinite(ratio.h) || ratio.w <= 0 || ratio.h <= 0) {
    throw new Error('Output contain ratio must have finite, positive sides')
  }
  if (!Number.isInteger(rootSize.width) || !Number.isInteger(rootSize.height)) {
    throw new Error('Output root size must use integer pixels')
  }

  const rootRatio = rootSize.width / rootSize.height
  const targetRatio = ratio.w / ratio.h
  const expectedHeight = rootSize.width / targetRatio
  const ratioMatchesWithinPixel = Math.abs(expectedHeight - rootSize.height) <= 1
  const size = ratioMatchesWithinPixel
    ? { ...rootSize }
    : rootRatio > targetRatio
      ? { width: rootSize.width, height: Math.ceil(expectedHeight) }
      : { width: Math.ceil(rootSize.height * targetRatio), height: rootSize.height }
  return {
    size,
    rootRect: {
      x: (size.width - rootSize.width) / 2,
      y: (size.height - rootSize.height) / 2,
      ...rootSize,
    },
  }
}

export function containedRootRect(rootSize: Size, containerSize: Size): Rect {
  assertPositiveSize(containerSize, 'Contain container')
  const natural = outputContainLayout(rootSize, { w: containerSize.width, h: containerSize.height })
  const scale = Math.min(
    containerSize.width / natural.size.width,
    containerSize.height / natural.size.height,
  )
  const width = rootSize.width * scale
  const height = rootSize.height * scale
  return {
    x: (containerSize.width - width) / 2,
    y: (containerSize.height - height) / 2,
    width,
    height,
  }
}

export function presetIdForSplit(split: SplitSettings): FixedOutputPresetId {
  if (split.kind === SplitKind.Grid) return OutputPresetId.Portrait
  return ratioEquals(split.tileRatio, CAROUSEL_TILE_RATIOS[0])
    ? OutputPresetId.Portrait
    : OutputPresetId.Square
}

export function validateOutputSettings(output: OutputSettings): void {
  getOutputPreset(output.presetId)
  assertSplitSettings(output.split)
  if (!/^#[0-9a-f]{6}$/i.test(output.background)) {
    throw new Error(`Output background must be a six-digit hex color, received "${String(output.background)}"`)
  }
  if (output.presetId === OutputPresetId.Story && output.split !== null) {
    throw new Error('Story output does not support carousel or grid splits')
  }
}
